import { observer } from 'mobx-react-lite'

import Styles from './AddStrategiesModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { LineChartUp01 } from 'blaze-ui/icons/16'
import { ContentStrategyTypeLabel } from '../../../constants'
import { BrandPlan, ContentStrategy, ContentStrategyType } from 'types'
import { GrowthSettingsModal } from '../GrowthSettingsModal/GrowthSettingsModal'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import {
  strategiesDefaultData,
  strategiesDefaultDataV2,
  mergeStrategyDefaults,
  Strategy,
} from 'screens/VirtualMarketerOnboarding/StrategiesSelection/utils'
import { useContentDefaults } from 'hooks/queries/useContentDefaults'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import dayjs from 'dayjs'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { AddStrategyModal } from './components/AddStrategyModal/AddStrategyModal'
import { ModalTrigger } from 'almanac-ui/Modal/ModalTrigger'
import { isActiveStrategy } from '../strategyUtils'
import { StrategyHeader } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/StrategyHeader/StrategyHeader'

const getDefaultContentStrategy = (
  type: ContentStrategyType,
  brandPlan: BrandPlan,
  mergedDefaults: typeof strategiesDefaultData,
): ContentStrategy | undefined => {
  const businessType = brandPlan.business_type ?? 'service'
  const defaults = mergedDefaults[businessType]
  return defaults?.strategies.find((s: Strategy) => s.id === type)
}

const StrategyRow = ({
  type,
  number,
  contentStrategy,
  brandPlan,
  isStarted,
  startDate,
  showLaunchNow = false,
}: {
  type: ContentStrategyType
  number: number
  contentStrategy: ContentStrategy | undefined
  brandPlan: BrandPlan
  isStarted: boolean
  startDate?: string
  showLaunchNow?: boolean
}) => {
  const { openModal } = useModals()
  const socialCount = contentStrategy?.crosspost_contents?.[0]?.count ?? 0
  const blogCount = contentStrategy?.seo_contents?.[0]?.count ?? 0
  const emailCount = contentStrategy?.email_contents?.[0]?.count ?? 0
  const shortFormVideoCount = contentStrategy?.video_crosspost_contents?.[0]?.count ?? 0
  const { creditsRemaining } = useCreditUsage()

  const { creditsNeeded } = useCreditsToGenerateContent(
    socialCount,
    blogCount,
    emailCount,
    shortFormVideoCount,
    0,
    0,
    0,
    0,
    {
      applyVisualStyle: true,
      percentageOfVisualStylesGeneration: 1,
    },
  )

  const strategy = {
    id: type,
    name: ContentStrategyTypeLabel[type],
    description: '',
    selected_goals: [],
    instagram_contents: contentStrategy?.instagram_contents ?? [],
    crosspost_contents: contentStrategy?.crosspost_contents ?? [],
    email_contents: contentStrategy?.email_contents ?? [],
    video_crosspost_contents: contentStrategy?.video_crosspost_contents ?? [],
    seo_contents: contentStrategy?.seo_contents ?? [],
    tactics: contentStrategy?.tactics ?? [],
    length: contentStrategy?.length ?? 1,
    start_date: dayjs().format('YYYY-MM-DD'),
  }

  const openStrategyLaunchModal = () => {
    openModal(AddStrategyModal, { strategy, brandPlan })
  }

  const launchStrategy = () => {
    if (creditsNeeded > creditsRemaining) {
      openModal(AutopilotUpgradeEntryModal, { variant: 'upgrade_to_growth', creditsNeeded, openStrategyLaunchModal })
      return
    }

    openStrategyLaunchModal()
  }

  return (
    <StrategyHeader
      strategy={strategy}
      onLaunch={showLaunchNow ? launchStrategy : undefined}
      startDate={startDate}
      number={number}
      creditsNeeded={creditsNeeded}
      isStarted={isStarted}
    />
  )
}

export const AddStrategiesModal = observer(() => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: contentDefaults } = useContentDefaults()

  if (!brandPlan || !contentDefaults) return null

  const mergedDefaults = mergeStrategyDefaults(
    brandPlan.post_content_types ? strategiesDefaultDataV2 : strategiesDefaultData,
    contentDefaults?.content_strategy_defaults,
  )

  const contentStrategiesEntries = brandPlan.content_strategies
    ? Object.entries(brandPlan.content_strategies).filter(
        (entry): entry is [ContentStrategyType, ContentStrategy] => entry[1] != null,
      )
    : []

  const activeStrategies = contentStrategiesEntries.filter(([, strategy]) => isActiveStrategy(strategy))
  const nextUpStrategies = contentStrategiesEntries
    .filter(([, strategy]) => !isActiveStrategy(strategy))
    .sort(([, a], [, b]) => dayjs(a.start_date).diff(dayjs(b.start_date)))

  const contentStrategyKeys = new Set(contentStrategiesEntries.map(([key]) => key))
  const businessType = brandPlan.business_type ?? 'service'
  const businessDefaults = mergedDefaults[businessType]
  const recommendedStrategies = businessDefaults
    ? businessDefaults.strategies
        .filter((s: Strategy) => !contentStrategyKeys.has(s.id))
        .map((s: Strategy) => s.id as ContentStrategyType)
    : []

  return (
    <Modal.Root size="md">
      <Modal.Header
        title="Add Strategies"
        actions={
          <div className={Styles.rightHeaderButtons}>
            <ModalTrigger modal={GrowthSettingsModal} brandPlan={brandPlan}>
              <Button frontIcon={LineChartUp01} variant="secondary">
                Change Growth Settings
              </Button>
            </ModalTrigger>
          </div>
        }
      ></Modal.Header>
      <Modal.Content withoutFooter>
        <div className={Styles.content}>
          {activeStrategies.length > 0 && (
            <div className={Styles.activeStrategies}>
              <Heading level={4}>Active Strategies</Heading>
              <div className={Styles.strategies}>
                {activeStrategies.map(([key, value], index) => {
                  return (
                    <StrategyRow
                      brandPlan={brandPlan}
                      key={key}
                      type={key as ContentStrategyType}
                      number={index + 1}
                      contentStrategy={value}
                      startDate={dayjs(value?.start_date || brandPlan?.start_date).format('ddd, MMM D YYYY')}
                      isStarted={dayjs(value?.start_date || brandPlan?.start_date).isBefore(dayjs())}
                    />
                  )
                })}
              </div>
            </div>
          )}
          {nextUpStrategies.length > 0 && (
            <div className={Styles.nextUpStrategies}>
              <Heading level={4}>Next up:</Heading>
              <div className={Styles.strategies}>
                {nextUpStrategies.map(([key, strategy], index) => (
                  <StrategyRow
                    brandPlan={brandPlan}
                    key={key}
                    type={key as ContentStrategyType}
                    number={activeStrategies.length + index + 1}
                    contentStrategy={strategy}
                    startDate={dayjs(strategy.start_date).format('ddd, MMM D YYYY')}
                    isStarted={false}
                    showLaunchNow={true}
                  />
                ))}
              </div>
            </div>
          )}
          {recommendedStrategies.length > 0 && (
            <div className={Styles.nextUpStrategies}>
              <Heading level={4}>Strategies we recommend:</Heading>
              <div className={Styles.strategies}>
                {recommendedStrategies.map((type, index) => (
                  <StrategyRow
                    brandPlan={brandPlan}
                    key={type}
                    type={type}
                    number={activeStrategies.length + nextUpStrategies.length + index + 1}
                    contentStrategy={getDefaultContentStrategy(type, brandPlan, mergedDefaults)}
                    isStarted={false}
                    showLaunchNow={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
